/**
 * Type definitions for Agent API integration
 * Matches backend types from src/server/types.ts
 */

// SSE payload shapes are contract-covered (contracts/src/sseTurn.ts +
// sseShared.ts) — single source shared with the backend and the CI gate.
export type {
  AgentInteractionRecord,
  AgentQuestion,
  BranchOption,
  CitationSource,
  CitationSourceKind,
  ConversationImage,
  QuestionOption,
} from '../../contracts/dist/index.js';

// Identity of a cited firm source, shared with the backend: it keys the chip's
// deep link and the per-bubble dedupe, which must not drift apart.
export { citationSourceKey } from '../../contracts/dist/index.js';

import type {
  AgentInteractionRecord,
  CitationSource,
  ConversationImage,
  DeployVersionEvent,
  TurnEvent,
} from '../../contracts/dist/index.js';

// Joins/splits multi-select answer labels; newline (not comma) because option
// labels can contain commas but never a newline.
export const MULTI_SELECT_DELIMITER = '\n';

// The conversation's single supervision state. 'max' shows every turn fully
// expanded; 'standard' folds each finished turn into a "Design process"
// dropdown, leaving only the final answer visible; 'min' additionally runs the
// turn hands-off (skips the interactive confirmation gates a fixed server-side
// list — CONFIRMATION_REQUIRED_TOOLS in src/agent/sdk-integration.ts —
// otherwise enforces). Persisted per-conversation via the settings PATCH
// (`PATCH /conversations/:id/settings`) and retargetable mid-turn via
// `POST /jobs/:id/verbosity`. Tuple + type single-sourced in
// contracts/src/vocabulary.ts, shared verbatim with src/server/types.ts.
export { VERBOSITY_LEVELS } from '../../contracts/dist/index.js';
export type { Verbosity } from '../../contracts/dist/index.js';

// A single entry in the agent's live plan. This SDK version builds the plan
// incrementally through the TaskCreate/TaskUpdate tools (there is no snapshot
// TodoWrite tool), so useAgentChat reconstructs the list from the tool stream
// into this shape. `content` is the task subject; `activeForm` is its
// present-continuous form, shown while the step is in progress.
export interface TodoItem {
  content: string;                                   // subject, e.g. "Trace rays"
  status: 'pending' | 'in_progress' | 'completed';
  activeForm: string;                                // present-continuous, e.g. "Tracing rays"
}

// SSE Event Types from agent backend. Skew handshake (`deploy_version`) is
// written as the raw first frame of every SSE stream ahead of sendSSE (see
// beginSseStream in src/server/utils.ts) — no `timestamp` field on the wire,
// and dispatched via a direct addEventListener in useAgentChat (not the
// generic `on()` helper, which matches on a `type` discriminant inside the
// JSON body that this handshake frame doesn't carry). Preserved here because
// TurnEvent (the turn stream proper) doesn't include it.
export type SSEEvent = TurnEvent | ({ type: 'deploy_version' } & DeployVersionEvent);

// Start Job Response — only response shape is consumed by frontend (agentApi.ts).
// The corresponding request shape lives backend-side in src/server/types.ts and
// is built field-by-field at the call site.
export interface StartJobResponse {
  job_id: string;
  status: 'queued' | 'running';
}

// File Attachment Types
export interface FileAttachment {
  id: string;
  name: string;
  type: string; // MIME type
  size: number;
  data: string; // base64 encoded data
  preview?: string; // preview URL for images
}

// Supported file types
export const SUPPORTED_FILE_TYPES = {
  // The four raster types the Claude API accepts in an image block. No
  // 'image/jpg' — it is not a real MIME type; canonicalMimeType folds it into
  // image/jpeg before any lookup here, so listing it only invited the two to
  // disagree about what counts as an image.
  IMAGE: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  DOCUMENT: ['application/pdf'],
  NOTEBOOK: ['application/x-ipynb+json'],
  TEXT: ['text/plain', 'text/markdown', 'text/csv', 'application/json']
} as const;

/** One tool / skill / subagent call the agent made.
 *
 *  Live calls are built from the turn's tool_start/tool_end SSE events and
 *  anchored to the character offset in the bubble's prose where they fired,
 *  exactly like `ConversationImage.offset`. Reopening a conversation instead rebuilds
 *  them from the stored SDK transcript, recovering the same offset by matching
 *  the transcript's assistant text blocks into the persisted bubble. */
export interface ToolCallRecord {
  id: string;
  name: string;
  kind: 'tool' | 'skill' | 'subagent';
  offset: number;
  input: unknown;
  startedAt: number;
  output?: unknown;
  isError?: boolean;
  durationMs?: number;
  /** Names the agent this call was made from — a subagent, or a side agent such
   *  as 'merit-function'. Absent on main-agent calls. */
  agentType?: string;
  /** Rebuilt calls only: the transcript records the call but no result, because
   *  the turn was interrupted. Distinct from a live call still running. */
  unresolved?: true;
  /** The reminders this result fed back into the model's context, as the one
   *  block the model received. Live it rides on the tool_end event; rebuilt it
   *  comes from the transcript's own `hook_additional_context` entry. */
  nudges?: string;
}

// Chat Message for UI
/** A `mirror_body_check` turn event without its envelope (contracts sseTurn.ts):
 *  what a system message carries live and in metadata.mirrorBodyCheck. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type MirrorBodyCheck = DistributiveOmit<Extract<TurnEvent, { type: 'mirror_body_check' }>, 'type' | 'message_id' | 'timestamp'>;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  // Email of the message author (only meaningful on user messages; absent on assistant/system)
  user_email?: string;
  authorLabel?: string;   // e.g. 'Main agent' for delegated-task bubbles
  // Set on the automated spec/goal judge's poke messages (stored as role:'user'
  // but authored by the judge, not the user) so the UI styles them distinctly.
  judge?: 'spec' | 'goal';
  // File attachments
  attachments?: FileAttachment[];
  // Additional metadata from SSE events
  error?: boolean;
  // True while the assistant turn that produced this bubble is still streaming
  // server-side; false once finalized. Drives the reconnect path: a finalized
  // bubble must not be blanked-and-rebuilt from replay (there is no live tail).
  streaming?: boolean;
  // Charts the turn's analysis tools rendered into this bubble, each carrying
  // the offset it belongs at, both live and after reloading history.
  images?: ConversationImage[];
  // Tool/skill/subagent calls attributed to this bubble. Admin-only display.
  toolCalls?: ToolCallRecord[];
  // A resolved interactive exchange. When set, the bubble renders as a read-only
  // InteractionCard instead of chat text (content is only the text-only fallback).
  interaction?: AgentInteractionRecord;
  // The server's mirror-body gate ended this turn without a passing check on a
  // system the turn wrote (failing, not re-checked, or not checkable). Renders
  // as MirrorBodyCheckNotice.
  mirrorBodyCheck?: MirrorBodyCheck;
  // Firm knowledge the agent used this turn (files/templates/playbooks it
  // actively consulted). Rendered as a collapsible citation chip below the
  // answer. Per-turn aggregate — not pinned to a sentence.
  sources?: CitationSource[];
  // Validated before restoring viewers; old message metadata may be malformed.
  codevAnalysisRuns?: unknown;
}
